import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * StackSelector – centered picker (modal + search).
 * Presentational: data/state stays in TextareaInput.
 * Works with any backend stack list ({ id, imageId, platformDefault }); nothing is hardcoded or parsed.
 */

const DEFAULT_ITEM = { id: "", imageId: "Platform default", isDefault: true };

const StackSelector = ({
    open,
    loading,
    error,
    stacks,
    selectedStackId,
    disabled,
    onToggle,
    onClose,
    onSelect,
    onRetry,
}) => {
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const triggerRef = useRef(null);
    const searchRef = useRef(null);
    const listRef = useRef(null);
    const wasOpenRef = useRef(false);

    const customStacks = useMemo(() => stacks.filter((s) => !s.platformDefault), [stacks]);
    const selected = customStacks.find((s) => s.id === selectedStackId);

    const items = useMemo(() => {
        const q = query.trim().toLowerCase();
        const filtered = q
            ? customStacks.filter((s) => (s.imageId || "").toLowerCase().includes(q))
            : customStacks;
        const showDefault = !q || DEFAULT_ITEM.imageId.toLowerCase().includes(q);
        return showDefault ? [DEFAULT_ITEM, ...filtered] : filtered;
    }, [customStacks, query]);

    // On open: reset search, start on the selected row, focus the search box, lock page scroll.
    useEffect(() => {
        if (!open) return undefined;
        setQuery("");
        const idx = selectedStackId
            ? Math.max(0, [DEFAULT_ITEM, ...customStacks].findIndex((s) => s.id === selectedStackId))
            : 0;
        setActive(idx);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        requestAnimationFrame(() => searchRef.current?.focus({ preventScroll: true }));
        return () => {
            document.body.style.overflow = prevOverflow;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Give focus back to the trigger when the picker closes.
    useEffect(() => {
        if (wasOpenRef.current && !open) triggerRef.current?.focus({ preventScroll: true });
        wasOpenRef.current = open;
    }, [open]);

    useEffect(() => {
        if (active >= items.length) setActive(Math.max(0, items.length - 1));
    }, [items.length, active]);

    // Keep the keyboard-active row visible.
    useEffect(() => {
        if (!open) return;
        listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" });
    }, [active, open]);

    const handleKeyDown = (e) => {
        if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            onClose();
            return;
        }
        if (!items.length) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((i) => Math.min(i + 1, items.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
        } else if (e.key === "Home" && e.target !== searchRef.current) {
            e.preventDefault();
            setActive(0);
        } else if (e.key === "End" && e.target !== searchRef.current) {
            e.preventDefault();
            setActive(items.length - 1);
        } else if (e.key === "Enter") {
            e.preventDefault();
            const pick = items[active];
            if (pick) onSelect(pick.id);
        }
    };

    const triggerTitle = selected ? `Stack: ${selected.imageId}` : "Stack: platform default";
    const hasCustomRows = items.some((i) => !i.isDefault);

    const renderRow = (item) => {
        const idx = items.indexOf(item);
        const isSelected = item.isDefault ? !selectedStackId : item.id === selectedStackId;
        return (
            <button
                key={item.isDefault ? "default" : item.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                data-idx={idx}
                className={`sa-stack-row ${isSelected ? "is-selected" : ""} ${idx === active ? "is-active" : ""}`}
                onMouseMove={() => active !== idx && setActive(idx)}
                onClick={() => onSelect(item.id)}
            >
                <span className="sa-stack-radio" aria-hidden="true" />
                <span className="sa-stack-row-copy">
                    <span className={`sa-stack-row-name ${item.isDefault ? "is-plain" : ""}`}>{item.imageId}</span>
                    {/* {item.isDefault && <span className="sa-stack-row-sub">Recommended setup</span>} */}
                </span>
            </button>
        );
    };

    return (
        <div className={`sa-stack ${open ? "is-open" : ""}`}>
            <button
                ref={triggerRef}
                type="button"
                className={`sa-stack-trigger ${selected ? "has-stack" : ""}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={onToggle}
                disabled={disabled}
                title={triggerTitle}
                aria-label={triggerTitle}
                aria-haspopup="dialog"
                aria-expanded={open}
            >
                <span className="sa-stack-trigger-icon">
                    <i className="fa-solid fa-gear sa-stack-gear" />
                </span>
                {selected && <span className="sa-stack-trigger-label">{selected.imageId}</span>}
            </button>

            {open &&
                createPortal(
                    <div className="sa-stack-overlay" onMouseDown={onClose} role="presentation">
                        <div
                            className="sa-stack-modal"
                            role="dialog"
                            aria-modal="true"
                            aria-label="Choose a stack"
                            onMouseDown={(e) => e.stopPropagation()}
                            onKeyDown={handleKeyDown}
                        >
                            <div className="sa-stack-modal-head">
                                <div>
                                    <h2 className="sa-stack-modal-title">Choose a stack</h2>
                                    <p className="sa-stack-modal-sub">Saved with your project when you start building.</p>
                                </div>
                                <button type="button" className="sa-stack-close" onClick={onClose} aria-label="Close">
                                    <i className="fa-solid fa-xmark" />
                                </button>
                            </div>

                            <div className="sa-stack-search">
                                <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
                                <input
                                    ref={searchRef}
                                    value={query}
                                    onChange={(e) => {
                                        setQuery(e.target.value);
                                        setActive(0);
                                    }}
                                    placeholder="Search stacks"
                                    aria-label="Search stacks"
                                    spellCheck={false}
                                    autoComplete="off"
                                />
                                {query && (
                                    <button
                                        type="button"
                                        className="sa-stack-search-clear"
                                        onClick={() => {
                                            setQuery("");
                                            searchRef.current?.focus();
                                        }}
                                        aria-label="Clear search"
                                    >
                                        <i className="fa-solid fa-xmark" />
                                    </button>
                                )}
                            </div>

                            <div className="sa-stack-list" ref={listRef} role="listbox" aria-label="Stacks">
                                {loading &&
                                    [0, 1, 2].map((n) => (
                                        <div key={n} className="sa-stack-skel">
                                            <span className="sa-stack-skel-line" style={{ width: `${68 - n * 12}%` }} />
                                        </div>
                                    ))}

                                {!loading && error && (
                                    <div className="sa-stack-state">
                                        <p>We couldn't load your stacks.</p>
                                        <button type="button" className="sa-stack-retry" onClick={onRetry}>
                                            Try again
                                        </button>
                                    </div>
                                )}

                                {!loading && !error && (
                                    <>
                                        {items.some((i) => i.isDefault) && (
                                            <>
                                                <div className="sa-stack-group">Default</div>
                                                {renderRow(items.find((i) => i.isDefault))}
                                            </>
                                        )}
                                        {hasCustomRows && (
                                            <>
                                                <div className="sa-stack-group">
                                                    Stacks <span className="sa-stack-group-count">{items.filter((i) => !i.isDefault).length}</span>
                                                </div>
                                                {items.filter((i) => !i.isDefault).map(renderRow)}
                                            </>
                                        )}
                                        {query && items.length === 0 && (
                                            <div className="sa-stack-state">
                                                <p>No stack matches "{query}".</p>
                                            </div>
                                        )}
                                        {!query && customStacks.length === 0 && (
                                            <div className="sa-stack-note">
                                                You haven't created any stacks yet, so the platform default will be used.
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className="sa-stack-modal-foot">
                                <span className="sa-stack-hint"><kbd>↑</kbd><kbd>↓</kbd> Navigate</span>
                                <span className="sa-stack-hint"><kbd>Enter</kbd> Select</span>
                                <span className="sa-stack-hint"><kbd>Esc</kbd> Close</span>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
};

export default StackSelector;